import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Table, Spin, Button, Space, message, Modal } from 'antd';
import { DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { getSignedUrl } from 'store/commonSlice';

function DocumentsTab({ tripDetails, loading, isV2 = false }) {
	const dispatch = useDispatch();
	const [previewVisible, setPreviewVisible] = useState(false);
	const [previewUrl, setPreviewUrl] = useState('');
	const [previewTitle, setPreviewTitle] = useState('');

	const extractUserFilename = (awsFilename) => {
		const parts = awsFilename.split('-');
		return parts.length > 5 ? parts.slice(5).join('-') : awsFilename;
	};
	/**
	 * Normalize a document field value to an array of string keys.
	 * Handles: string, object with document_key, array of strings,
	 * array of objects with document_key, and null/undefined.
	 */
	const normalizeDocKeys = (value) => {
		if (!value) return [];
		if (typeof value === 'string') return [value];
		if (!Array.isArray(value) && typeof value === 'object' && value.document_key) {
			return [value.document_key];
		}
		if (Array.isArray(value)) {
			return value
				.map((item) => {
					if (typeof item === 'string') return item;
					if (item && typeof item === 'object' && item.document_key)
						return item.document_key;
					return null;
				})
				.filter(Boolean);
		}
		return [];
	};

	// eslint-disable-next-line sonarjs/cognitive-complexity
	const documentsData = React.useMemo(() => {
		if (!tripDetails) return [];
		const documents = [];

		// --- Odometer Start ---
		const odometerStartKeys = normalizeDocKeys(tripDetails.trip_documents?.odometer_start);
		odometerStartKeys.forEach((doc, index) => {
			documents.push({
				id: `odometer_start_${index}`,
				filename: doc,
				displayName: extractUserFilename(doc),
				document_type: 'Odometer Start',
			});
		});

		// --- Odometer End ---
		const odometerEndKeys = normalizeDocKeys(tripDetails.trip_documents?.odometer_end);
		odometerEndKeys.forEach((doc, index) => {
			documents.push({
				id: `odometer_end_${index}`,
				filename: doc,
				displayName: extractUserFilename(doc),
				document_type: 'Odometer End',
			});
		});

		// --- Additional Charges Proof ---
		if (isV2) {
			const additionalChargesKeys = normalizeDocKeys(
				tripDetails.trip_documents?.additional_charges_proof_key
			);
			additionalChargesKeys.forEach((doc, index) => {
				documents.push({
					id: `additional_charges_proof_${index}`,
					filename: doc,
					displayName: extractUserFilename(doc),
					document_type: 'Additional Charges Proof',
					entity: tripDetails.interCity ? 'proof_of_delivery_upload' : 'trip',
				});
			});
		}

		// --- Proof of Request ---
		// The PoR key lives on trip info (info.requirement_proof). The trip_documents fields are
		// read as well for older data. info and top-level keys and the status are V2 only, so V1 is
		// unchanged. Keys are de-duplicated across the sources.
		const proofOfRequestKeys = Array.from(
			new Set([
				...normalizeDocKeys(tripDetails.trip_documents?.proof_of_request),
				...normalizeDocKeys(tripDetails.trip_documents?.requirement_proof),
				...(isV2
					? [
							...normalizeDocKeys(tripDetails.info?.requirement_proof),
							...normalizeDocKeys(tripDetails.requirement_proof),
					  ]
					: []),
			])
		);
		const porStatus =
			isV2 && tripDetails.por_approval_status
				? `${tripDetails.por_approval_status}`.replace(/^./, (c) => c.toUpperCase())
				: null;
		proofOfRequestKeys.forEach((doc, index) => {
			documents.push({
				id: `proof_of_request_${index}`,
				filename: doc,
				displayName: extractUserFilename(doc),
				document_type: porStatus ? `Proof of Request (${porStatus})` : 'Proof of Request',
			});
		});

		// --- Per Order Supporting Evidence ---
		const perOrderProofFromUrls = Array.isArray(tripDetails.per_order_proof_urls)
			? tripDetails.per_order_proof_urls.map((p) => p?.key).filter(Boolean)
			: [];
		const perOrderProofKeys = normalizeDocKeys(
			tripDetails.per_order_proof_keys || tripDetails.info?.per_order_proof_keys
		);
		Array.from(new Set([...perOrderProofKeys, ...perOrderProofFromUrls])).forEach(
			(doc, index) => {
				documents.push({
					id: `per_order_proof_${index}`,
					filename: doc,
					displayName: extractUserFilename(doc),
					document_type: 'Per Order Supporting Evidence',
				});
			}
		);

		// --- Toll Receipts ---
		if (tripDetails.toll_info && Array.isArray(tripDetails.toll_info)) {
			tripDetails.toll_info.forEach((toll, index) => {
				const tollDocKeys = normalizeDocKeys(toll.toll_doc);
				tollDocKeys.forEach((doc, receiptIndex) => {
					documents.push({
						id: `toll_receipt_${index}_${receiptIndex}`,
						filename: doc,
						displayName: extractUserFilename(doc),
						document_type: 'Toll Receipt',
					});
				});
			});
		}

		return documents;
	}, [tripDetails, isV2]);
	const columns = [
		{
			title: 'Filename',
			dataIndex: 'displayName',
			key: 'displayName',
			render: (text) => text || '-',
			width: '40%',
		},
		{
			title: 'Document Type',
			dataIndex: 'document_type',
			key: 'document_type',
			render: (text) => text || '-',
			width: '40%',
		},
		{
			title: 'Actions',
			key: 'actions',
			width: '20%',
			align: 'center',
			render: (_, record) => (
				<Space size="small">
					<Button
						type="text"
						size="small"
						icon={<DownloadOutlined />}
						onClick={() => handleDownload(record)}
						title="Download"
						style={{ color: '#1890ff' }}
					/>
					<Button
						type="text"
						size="small"
						icon={<EyeOutlined />}
						onClick={() => handleView(record)}
						title="View"
						style={{ color: '#1890ff' }}
					/>
				</Space>
			),
		},
	];

	const handleDownload = async (record) => {
		if (!record.filename) {
			message.error('Document filename not available');
			return;
		}

		try {
			const data = {
				url_type: 'download',
				data: {
					entity: isV2 ? record.entity || 'trip' : 'trip',
					keys: [record.filename],
				},
			};

			const response = await dispatch(getSignedUrl(data));
			if (response?.data && response.data[0]?.url) {
				window.open(response.data[0].url, '_blank');
			} else {
				message.error('Failed to get download URL');
			}
		} catch (error) {
			console.error('Download error:', error);
			message.error('Failed to download document');
		}
	};

	const handleView = async (record) => {
		if (!record.filename) {
			message.error('Document filename not available');
			return;
		}

		try {
			// Reset previous preview state before fetching new URL
			setPreviewVisible(false);
			setPreviewUrl('');
			setPreviewTitle('');
			const data = {
				url_type: 'view',
				data: {
					entity: isV2 ? record.entity || 'trip' : 'trip',
					keys: [record.filename],
				},
			};

			const response = await dispatch(getSignedUrl(data));
			if (response?.data && response.data[0]?.url) {
				setPreviewUrl(response.data[0].url);
				setPreviewTitle(record.displayName || record.filename);
				setPreviewVisible(true);
			} else {
				message.error('Failed to get view URL');
			}
		} catch (error) {
			console.error('View error:', error);
			message.error('Failed to view document');
		}
	};

	const getFileExtension = (filename) => filename.split('.').pop().toLowerCase();

	const renderPreviewContent = () => {
		const fileExt = getFileExtension(previewTitle);

		if (['jpg', 'jpeg', 'png', 'gif'].includes(fileExt)) {
			return (
				<img
					alt={previewTitle}
					style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
					src={previewUrl}
				/>
			);
		}
		if (fileExt === 'pdf') {
			return (
				<div style={{ textAlign: 'center', padding: '20px' }}>
					<div style={{ marginBottom: '20px' }}>
						<p style={{ fontSize: '16px', marginBottom: '10px' }}>
							PDF Preview: {previewTitle}
						</p>
						<p style={{ color: '#666', marginBottom: '20px' }}>
							For better PDF viewing experience, please open in a new tab
						</p>
					</div>
					<div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
						<Button
							type="primary"
							onClick={() => window.open(previewUrl, '_blank')}
							size="large"
						>
							Open PDF in New Tab
						</Button>
					</div>
					<div
						style={{
							marginTop: '20px',
							border: '1px solid #d9d9d9',
							borderRadius: '4px',
						}}
					>
						<iframe
							title={previewTitle}
							src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=0`}
							width="100%"
							height="400px"
							style={{ border: 'none' }}
							onError={() => {
								// If iframe fails, it will be hidden and user can use buttons above
							}}
						/>
					</div>
				</div>
			);
		}
		return (
			<div style={{ textAlign: 'center', padding: '20px' }}>
				<p>Preview not available for this file type.</p>
				<Button type="primary" onClick={() => window.open(previewUrl, '_blank')}>
					Open in New Tab
				</Button>
			</div>
		);
	};

	if (loading) {
		return (
			<div style={{ textAlign: 'center', padding: '50px 0' }}>
				<Spin size="large" tip="Loading documents..." />
			</div>
		);
	}

	return (
		<div style={{ padding: '16px 0' }}>
			<Table
				columns={columns}
				dataSource={documentsData || []}
				rowKey="id"
				pagination={false}
				size="middle"
				bordered
				locale={{
					emptyText: 'No documents found',
				}}
				style={{
					backgroundColor: '#fff',
					borderRadius: 8,
				}}
			/>

			<Modal
				open={previewVisible}
				title={previewTitle}
				footer={null}
				onCancel={() => {
					setPreviewVisible(false);
					setPreviewUrl('');
					setPreviewTitle('');
				}}
				centered
				width={getFileExtension(previewTitle) === 'pdf' ? 1000 : 800}
				bodyStyle={{ textAlign: 'center', background: '#fff' }}
				destroyOnClose
			>
				{renderPreviewContent()}
			</Modal>
		</div>
	);
}

DocumentsTab.propTypes = {
	tripDetails: PropTypes.shape({
		interCity: PropTypes.bool,
		por_approval_status: PropTypes.string,
		requirement_proof: PropTypes.any,
		odometer_end: PropTypes.array,
		per_order_proof_keys: PropTypes.any,
		per_order_proof_urls: PropTypes.arrayOf(
			PropTypes.shape({
				key: PropTypes.string,
				url: PropTypes.string,
			})
		),
		trip_documents: PropTypes.shape({
			additional_charges_proof_key: PropTypes.any,
			odometer_start: PropTypes.any,
			odometer_end: PropTypes.any,
			trip_breakdown: PropTypes.any,
			proof_of_request: PropTypes.any,
			requirement_proof: PropTypes.any,
		}),
		info: PropTypes.shape({
			requirement_proof: PropTypes.array,
			odometer_end: PropTypes.array,
			per_order_proof_keys: PropTypes.any,
		}),
		toll_info: PropTypes.arrayOf(
			PropTypes.shape({
				toll_doc: PropTypes.array,
			})
		),
	}),
	loading: PropTypes.bool,
	isV2: PropTypes.bool,
};

export default DocumentsTab;
